import React, { useState } from 'react';
import { Package, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';

export default function ContainerAnalysisTable({ data }) {
  const [sortBy, setSortBy] = useState('failureRate'); // failureRate, total, avgRetries
  const [sortOrder, setSortOrder] = useState('desc');

  if (!data || data?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <Package className="w-6 h-6 text-purple-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Analyse par Conteneur</h3>
        </div>
        <p className="text-gray-500 text-center py-8">Aucune donnée de conteneur disponible</p>
      </div>
    );
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortedData = [...data]?.sort((a, b) => {
    const aVal = parseFloat(a?.[sortBy]) || 0;
    const bVal = parseFloat(b?.[sortBy]) || 0;
    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const getFailureRateColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate >= 50) return 'text-red-600 bg-red-50';
    if (numRate >= 25) return 'text-orange-600 bg-orange-50';
    if (numRate > 0) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Package className="w-6 h-6 text-purple-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Analyse par Conteneur (Top 10)</h3>
        </div>
        <div className="text-sm text-gray-600">
          {data?.length} conteneur(s) analysé(s)
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Conteneur
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('total')}
              >
                <div className="flex items-center">
                  Total Webhooks
                  {sortBy === 'total' && (
                    sortOrder === 'asc' ? <TrendingUp className="w-4 h-4 ml-1" /> : <TrendingDown className="w-4 h-4 ml-1" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Succès / Échecs
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('failureRate')}
              >
                <div className="flex items-center">
                  Taux d'Échec
                  {sortBy === 'failureRate' && (
                    sortOrder === 'asc' ? <TrendingUp className="w-4 h-4 ml-1" /> : <TrendingDown className="w-4 h-4 ml-1" />
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('avgRetries')}
              >
                <div className="flex items-center">
                  Moy. Relances
                  {sortBy === 'avgRetries' && (
                    sortOrder === 'asc' ? <TrendingUp className="w-4 h-4 ml-1" /> : <TrendingDown className="w-4 h-4 ml-1" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData?.map((container) => (
              <tr key={container?.containerId} className="hover:bg-gray-50">
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{container?.containerReference}</div>
                  <div className="text-xs text-gray-500">{container?.containerCode}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm font-semibold text-gray-900">{container?.total}</span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-green-600 font-medium">{container?.success}</span>
                    <span className="text-gray-400">/</span>
                    <span className="text-sm text-red-600 font-medium">{container?.failed}</span>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getFailureRateColor(container?.failureRate)}`}>
                    {container?.failureRate}%
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">{container?.avgRetries}</span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-xs text-gray-600 uppercase">{container?.containerStatus}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* High Risk Containers Alert */}
      {sortedData?.filter(c => parseFloat(c?.failureRate) >= 50)?.length > 0 && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">
              ⚠️ {sortedData?.filter(c => parseFloat(c?.failureRate) >= 50)?.length} conteneur(s) à risque élevé
            </p>
            <p className="text-xs text-red-600 mt-1">
              Taux d'échec ≥ 50% - Action requise
            </p>
          </div>
        </div>
      )}
    </div>
  );
}